import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ClockInPrompt — pops once per session after login when a staff member
 * isn't yet clocked in. Big, friendly modal with their name highlighted
 * and a single "Clock In Now" call-to-action.
 *
 * Triggering rules:
 *   - User is authenticated AND has a `user.id`
 *   - We haven't already prompted them this session (sessionStorage key)
 *   - GET /time-punches/status reports `is_clocked_in === false`
 *   - The user has employee-zone access (i.e. the punch endpoint accepts
 *     them). The HTTP error case silently bails out — we never block
 *     anyone from working over a clock-prompt failure.
 *
 * Cancel just dismisses for the session; the regular Employee Zone tab
 * still lets them clock in later.
 */
const ClockInPrompt = () => {
  const { user, token, isAuthenticated, selectedLocationId, getSelectedLocation } = useAuth();
  const [open, setOpen] = useState(false);
  const [punching, setPunching] = useState(false);

  // Key includes a token fingerprint so a logout-then-login cycle in
  // the same browser tab triggers a fresh prompt — sessionStorage on
  // its own would suppress the second login.
  const tokenFingerprint = token ? token.slice(-12) : '';
  const sessionKey = user?.id ? `clock-in-prompt:${user.id}:${tokenFingerprint}` : null;

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !token) return;
    if (!sessionKey) return;
    // Only prompt ONCE per browser session per user — they can ignore it
    // on subsequent page loads without us nagging them.
    if (sessionStorage.getItem(sessionKey) === 'shown') return;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/employee-zone/time-punches/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.data && !res.data.is_clocked_in) {
          // Treats both `false` and `null` (user has never punched) as
          // "not clocked in" — both warrant the prompt.
          setOpen(true);
          sessionStorage.setItem(sessionKey, 'shown');
        }
      } catch {
        // Silent — don't block the UI just because the prompt failed.
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id, token, sessionKey]);

  const handleClockIn = useCallback(async () => {
    setPunching(true);
    const location = getSelectedLocation();
    try {
      await axios.post(`${API}/api/employee-zone/time-punches`, {
        punch_type: 'in',
        location_id: selectedLocationId,
        location_name: location?.name,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Clocked in — have a great shift!');
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setPunching(false);
    }
  }, [token, selectedLocationId, getSelectedLocation]);

  const handleCancel = () => setOpen(false);

  if (!isAuthenticated || !user?.id) return null;

  // Prefer full_name → first name → username
  const displayName = user.full_name || user.first_name || user.username || 'there';
  const firstWord = String(displayName).trim().split(/\s+/)[0];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleCancel(); }}>
      <DialogContent
        className="bg-slate-900 border-2 border-emerald-500 text-white sm:max-w-md shadow-[0_0_40px_rgba(16,185,129,0.35)]"
        data-testid="clock-in-prompt-dialog"
        onEscapeKeyDown={handleCancel}
      >
        <div className="flex flex-col items-center text-center py-2">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 border-2 border-emerald-500 flex items-center justify-center mb-4">
            <Clock className="w-8 h-8 text-emerald-300" data-testid="clock-in-prompt-icon" />
          </div>

          {/* HUGE highlighted name */}
          <p
            className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-2"
          >Welcome back</p>
          <h2
            className="text-4xl sm:text-5xl font-extrabold leading-tight mb-4 bg-gradient-to-r from-emerald-300 via-yellow-300 to-pink-300 bg-clip-text text-transparent break-words max-w-full"
            data-testid="clock-in-prompt-name"
          >{firstWord}</h2>

          <p
            className="text-lg sm:text-xl text-slate-200 mb-7"
            data-testid="clock-in-prompt-question"
          >
            Do you want to clock in?
          </p>

          <div className="flex flex-col-reverse sm:flex-row gap-3 w-full">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={punching}
              className="flex-1 bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
              data-testid="clock-in-prompt-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleClockIn}
              disabled={punching}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-base shadow-lg"
              data-testid="clock-in-prompt-confirm"
            >
              {punching ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Clock className="w-4 h-4 mr-2" />
              )}
              Clock In Now
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClockInPrompt;
